/**
 * Generic HTTP client for the builder API.
 *
 * Owns the JWT token lifecycle: read from localStorage on every call,
 * inject as `Authorization: Bearer …`, and broadcast `auth:expired`
 * when the server returns 401 so `useAuth` can sign the user out.
 *
 * All error responses raise an `ApiError` with the parsed payload so
 * pages can render structured messages (e.g. validation errors).
 */

import type { ApiError } from "@/types";
import { slugFromPath, tenantPrefix, tokenKeyFor } from "./tenant-path";

// Storage is keyed PER CUSTOMER since Plan E: one fleet address means
// one origin, hence one localStorage shared by every customer. See
// `lib/tenant-path.ts` for what a single shared key would cost.
const AUTH_EXPIRED_EVENT = "builder:auth-expired";
// Set when a 401 kicks the user out so the login page can explain it
// ("tu sesión expiró") instead of showing a bare form. Holds the
// expiry timestamp — see ``wasSessionRecentlyExpired``.
const SESSION_EXPIRED_KEY = "builder.sessionExpired";
// Deliberately NOT keyed per customer, unlike auth_token (E4): this is
// sessionStorage — per TAB — holding an ephemeral 60s flag with no
// customer data in it. A tab shows one customer at a time, so there is
// no cross-customer collision for a per-tab flag, and the fleet-wide
// login page (which READS it, outside any prefix) could not know which
// slug to read a keyed version under.
const SESSION_EXPIRED_TTL_MS = 60_000;

// ── Token storage ─────────────────────────────────────────

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  const slug = slugFromPath();
  if (slug === null) return null;
  return window.localStorage.getItem(tokenKeyFor(slug));
}

/**
 * Store or clear the session for one customer.
 *
 * `slug` is required when storing and comes from the login response:
 * at that moment the URL is still the unprefixed login page, so there
 * is nothing in the path to derive it from. Clearing uses the current
 * path — signing out of one customer must not sign the person out of
 * another company they also work with.
 */
export function setToken(token: string | null, slug?: string): void {
  if (typeof window === "undefined") return;
  if (token) {
    if (!slug) {
      throw new Error("setToken needs the customer the token was minted for");
    }
    window.localStorage.setItem(tokenKeyFor(slug), token);
    // A successful sign-in resolves the expiry notice. Only on a real
    // token: ``setToken(null)`` also runs on the expiry path itself,
    // which would wipe the flag right after it was set.
    try {
      window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    } catch {
      /* ignore */
    }
  } else {
    const current = slugFromPath();
    if (current !== null) window.localStorage.removeItem(tokenKeyFor(current));
  }
}

export function onAuthExpired(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(AUTH_EXPIRED_EVENT, handler);
  return () => {
    window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  };
}

function emitAuthExpired(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_EXPIRED_KEY, String(Date.now()));
  } catch {
    /* private mode — the login notice is a nicety, not a requirement */
  }
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

/**
 * True when a 401 bounced the user here within the last minute.
 *
 * Deliberately does NOT clear the flag — the login page can mount more
 * than once during the bounce (route swap first, hard reload after),
 * and a read-and-clear consumes it on the throwaway mount. Cleared by
 * ``setToken`` on a successful sign-in; the TTL prevents a stale notice.
 */
export function wasSessionRecentlyExpired(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.sessionStorage.getItem(SESSION_EXPIRED_KEY);
    if (!raw) return false;
    const stampedAt = Number(raw);
    if (!Number.isFinite(stampedAt)) return false;
    if (Date.now() - stampedAt > SESSION_EXPIRED_TTL_MS) {
      window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Active UI locale — same localStorage key react-i18next writes. */
function getActiveLocale(): "en" | "es" | "pt" {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem("i18nextLng")?.toLowerCase();
    if (stored?.startsWith("es")) return "es";
    if (stored?.startsWith("pt")) return "pt";
  } catch {
    /* ignore */
  }
  return "en";
}

/** Localized "your session ended" — used outside React (request layer). */
export function sessionExpiredMessage(): string {
  return {
    en: "Your session ended. Please sign in again.",
    es: "Tu sesión expiró. Inicia sesión de nuevo.",
    pt: "Sua sessão expirou. Entre novamente.",
  }[getActiveLocale()];
}

/** Localized "can't reach the server" for transport failures. */
export function connectionLostMessage(): string {
  return {
    en: "Lost connection to the server. Please try again in a moment.",
    es: "Se perdió la conexión con el servidor. Reintenta en un momento.",
    pt: "A conexão com o servidor foi perdida. Tente de novo em instantes.",
  }[getActiveLocale()];
}

/** Localized "the answer arrived broken" — a 2xx whose JSON body does not parse. */
export function invalidResponseMessage(status: number): string {
  return {
    en: `The server's response arrived incomplete or corrupted (HTTP ${status}). Please try again in a moment.`,
    es: `La respuesta del servidor llegó incompleta o dañada (HTTP ${status}). Reintenta en un momento.`,
    pt: `A resposta do servidor chegou incompleta ou corrompida (HTTP ${status}). Tente de novo em instantes.`,
  }[getActiveLocale()];
}

/**
 * ``fetch`` wrapper that turns transport-level failures into a human,
 * localized message. Native ``fetch`` throws ``TypeError: Failed to
 * fetch`` when the server is unreachable (mid-deploy, offline, CORS) —
 * without this, that raw string leaks into the UI. ``AbortError`` is
 * re-thrown untouched so request cancellation keeps working.
 */
async function safeFetch(
  input: string,
  init?: RequestInit,
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    // Keep the original TypeError in the console so a genuine network
    // problem stays diagnosable; the UI only ever sees the human text.
    console.warn("[api] network request failed", input, err);
    throw new Error(connectionLostMessage());
  }
}

// ── Error type ────────────────────────────────────────────

export class HttpError extends Error implements ApiError {
  status: number;
  detail?: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.detail = detail;
  }
}

// ── Core request ──────────────────────────────────────────

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Skip the `Authorization` header (e.g. for `/api/auth/login`). */
  anonymous?: boolean;
}

/**
 * Paths that must NOT carry the customer prefix (Plan E, E4).
 *
 * MIRRORS `tenancy.resolve.TENANT_OPTIONAL_PATHS`, an EXACT-MATCH set
 * on the server: prefixing the login stops it matching there, and the
 * fleet-wide login page 404s instead of authenticating.
 */
const UNPREFIXED_PATHS = new Set(["/api/auth/login"]);

/** The one screen that lives outside every customer prefix. */
export const LOGIN_PATH = "/login";

/**
 * Leave for the fleet-wide login page (Plan E, E4).
 *
 * A FULL navigation, never `navigate("/login")`. The router runs with
 * `basename="/{slug}"`, so a client-side navigation would resolve to
 * `/{slug}/login` — a route that does not exist and never will: the
 * login is deliberately the one screen OUTSIDE every customer prefix,
 * because it is the page you reach before anyone knows which customer
 * you belong to.
 */
export function goToLogin(): void {
  window.location.replace(LOGIN_PATH);
}


function buildUrl(
  path: string,
  query?: RequestOptions["query"],
): string {
  const bare = path.startsWith("/") ? path : `/${path}`;
  // Single point for all ~70 routes: unlike the portal, nothing here
  // bypasses `request()`.
  const url = UNPREFIXED_PATHS.has(bare) ? bare : `${tenantPrefix()}${bare}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, query, signal, anonymous = false } = options;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  // A FormData body (file upload) goes through untouched: the browser
  // sets `multipart/form-data` with its boundary. JSON.stringify would
  // turn it into "{}" and the file would never leave.
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";

  const token = anonymous ? null : getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await safeFetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    signal,
  });

  if (response.status === 401 && !anonymous) {
    emitAuthExpired();
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson
    ? await response.json().catch((err: unknown) => {
        // !ok: the body is only read for its message; extractMessage
        // falls back to statusText when it does not parse.
        if (!response.ok) return null;
        // A 2xx whose body does not parse is a failure, never `null`
        // typed as T (owner decision, audit #269). Aborts keep
        // propagating untouched so cancellation still works.
        if (err instanceof DOMException && err.name === "AbortError") throw err;
        console.warn("[api] unreadable JSON body", path, err);
        throw new HttpError(response.status, invalidResponseMessage(response.status), err);
      })
    : await response.text().catch(() => "");

  if (!response.ok) {
    // An authenticated 401 is an expired/invalid session — show the
    // friendly notice, never the backend's raw "Token has expired" /
    // "Invalid token". A 401 on an anonymous call is a wrong password,
    // so that one keeps the backend's own message.
    const message =
      response.status === 401 && !anonymous
        ? sessionExpiredMessage()
        : extractMessage(payload, response.statusText);
    throw new HttpError(response.status, message, payload);
  }

  return payload as T;
}

function extractMessage(payload: unknown, fallback: string): string {
  if (typeof payload === "string" && payload) return payload;
  if (payload && typeof payload === "object") {
    const detail = (payload as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object") {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string") return message;
    }
  }
  return fallback || "Request failed";
}

// ── Convenience verb wrappers ─────────────────────────────

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, "method" | "body">,
  ) => request<T>(path, { ...options, method: "POST", body }),
  put: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, "method" | "body">,
  ) => request<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, "method" | "body">,
  ) => request<T>(path, { ...options, method: "PATCH", body }),
  // DELETE accepts an optional JSON body — the permission-overrides
  // endpoint (PR-3) takes the list of override keys to remove in the
  // request body. Existing body-less callers are unaffected.
  delete: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, "method" | "body">,
  ) => request<T>(path, { ...options, method: "DELETE", body }),
};
